/**
 * BuilderCard Component - Enhanced builder integration card with modern UI
 */
import { useState, useEffect } from '@wordpress/element';
import { useDispatch } from '@wordpress/data';
import { __ } from '@wordpress/i18n';
import {
    Card,
    Switch,
    Badge,
    Collapse,
    Space,
    Typography,
    Divider,
    Row,
    Col,
    notification,
    Tooltip,
    Tag,
    Avatar,
} from 'antd';
import {
    CodeOutlined,
    StarOutlined,
    FireOutlined,
    CrownOutlined,
} from '@ant-design/icons';

const { Text, Paragraph } = Typography;
import {ElementorIcon } from "../../icons/index.js";
import {GutenbergIcon} from "../../icons/index.js";
const BuilderCard = ({ builder, config, settings, onToggle, onElementToggle }) => {
    const [isTesting, setIsTesting] = useState(false);
    const { testBuilderIntegration } = useDispatch('marqueex/settings');

    const getBuilderConfig = (builderName) => {
        const configs = {
            elementor: {
                name: 'Elementor',
                icon: <ElementorIcon activeColor={settings.enabled ? '#fff' : '#e91e63'}/>,
                color: '#e91e63',
                gradient: 'linear-gradient(135deg, #e91e63 0%, #f06292 100%)',
                tag: 'Popular',
                tagColor: 'magenta',
                description: __('Professional drag-and-drop page builder with advanced widgets and live editing.', 'marqueex'),
                features: ['Live Editing', 'Responsive Design', 'Advanced Widgets', 'Theme Builder']
            },
            gutenberg: {
                name: 'Gutenberg',
                icon: <GutenbergIcon activeColor={settings.enabled ? '#fff' : '#0073aa'}/>,
                color: '#0073aa',
                gradient: 'linear-gradient(135deg, #0073aa 0%, #005177 100%)',
                tag: 'Native',
                tagColor: 'blue',
                description: __('WordPress native block editor with seamless content creation experience.', 'marqueex'),
                features: ['Block Editor', 'Native WordPress', 'Content Blocks', 'Pattern Library']
            },
            bricks: {
                name: 'Bricks',
                icon: <CrownOutlined />,
                color: '#ff6b35',
                gradient: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
                tag: 'Premium',
                tagColor: 'orange',
                description: __('Advanced visual builder with unlimited design possibilities and performance focus.', 'marqueex'),
                features: ['Visual Builder', 'Performance', 'Custom Code', 'Dynamic Data']
            },
            shortcode: {
                name: 'Shortcode',
                icon: <CodeOutlined />,
                color: '#52c41a',
                gradient: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                tag: 'Flexible',
                tagColor: 'green',
                description: __('Universal shortcodes for maximum flexibility across any theme or page builder.', 'marqueex'),
                features: ['Universal', 'Theme Agnostic', 'Easy Integration', 'Lightweight']
            }
        };
        return configs[builderName] || configs.shortcode;
    };

    const builderConfig = getBuilderConfig(builder);

    const getCompatibilityScore = () => {
        const totalFeatures = (config.elements ? Object.keys(config.elements).length : 0);
        const enabledFeatures = Object.values(settings.elements || {}).filter(Boolean).length;
        return totalFeatures > 0 ? Math.round((enabledFeatures / totalFeatures) * 100) : 100;
    };

    const getStatusInfo = () => {
        if (settings.enabled) {
            const score = getCompatibilityScore();
            return {
                status: 'success',
                text: __('Active', 'marqueex'),
                color: builderConfig.color,
                score
            };
        }
        return {
            status: 'default',
            text: __('Inactive', 'marqueex'),
            color: '#d9d9d9',
            score: 0
        };
    };

    const handleTestIntegration = async () => {
        setIsTesting(true);
        try {
            const result = await testBuilderIntegration(builder);
            notification[result.status === 'compatible' ? 'success' : 'error']({
                message: __('Integration Test', 'marqueex'),
                description: result.message,
                placement: 'topRight',
                duration: 4.5,
            });
        } catch (error) {
            notification.error({
                message: __('Integration Test Failed', 'marqueex'),
                description: error.message,
                placement: 'topRight',
            });
        } finally {
            setIsTesting(false);
        }
    };

    const statusInfo = getStatusInfo();
    const cardClassName = `marqueex-builder-card marqueex-builder-${builder} ${settings.enabled ? 'marqueex-builder-active' : 'marqueex-builder-inactive'}`;

    const cardStyle = {
        marginBottom: 24,
        borderRadius: 12,
        overflow: 'hidden',
        border: settings.enabled ? `2px solid ${builderConfig.color}` : '1px solid #f0f0f0',
        boxShadow: settings.enabled
            ? `0 4px 20px ${builderConfig.color}20`
            : '0 2px 8px rgba(0,0,0,0.06)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative'
    };

    return (
      <Card
        className={cardClassName}
        style={cardStyle}
        styles={{ body: { padding: 0 } }}
        variant="borderless"
      >
        {/* Header Section with Gradient */}
        <div style={{
          background: settings.enabled ? builderConfig.gradient : 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
          padding: '20px 24px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Background Pattern */}
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '100px',
            height: '100px',
            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
            borderRadius: '50%',
            transform: 'translate(30px, -30px)'
          }} />

          <Row align="middle" justify="space-between">
            <Col>
              <Space size="large" align="center">
                <Avatar
                  size={48}
                  style={{
                    backgroundColor: settings.enabled ? 'rgba(255,255,255,0.2)' : '#f0f0f0',
                    color: settings.enabled ? '#fff' : builderConfig.color,
                    fontSize: '20px',
                    border: settings.enabled ? '2px solid rgba(255,255,255,0.3)' : 'none'
                  }}
                  icon={builderConfig.icon}
                />
                <div>
                  <Space size="small" align="center">
                    <Text style={{
                      fontSize: '18px',
                      fontWeight: 600,
                      color: settings.enabled ? '#fff' : '#262626',
                      textShadow: settings.enabled ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                    }}>
                      {builder.charAt(0).toUpperCase() + builder.slice(1)}
                    </Text>
                    <Tag color={builderConfig.tagColor} style={{ margin: 0 }}>
                      {builderConfig.tag}
                    </Tag>
                  </Space>
                  <div style={{ marginTop: 4 }}>
                    <Badge
                      status={statusInfo.status}
                      text={
                        <Text style={{
                          color: settings.enabled ? 'rgba(255,255,255,0.9)' : '#666',
                          fontSize: '12px'
                        }}>
                          {statusInfo.text}
                        </Text>
                      }
                    />
                  </div>
                </div>
              </Space>
            </Col>
            <Col>
              <Space size="middle" align="center">
                <Space direction="vertical" size="small">
                  <Tooltip title={settings.enabled ? __("Disable Integration", "marqueex") : __("Enable Integration", "marqueex")}>
                    <Switch
                      checked={settings.enabled}
                      onChange={(checked) => onToggle(builder, "enabled", checked)}
                      style={{
                        backgroundColor: settings.enabled ? 'rgba(255,255,255,0.3)' : undefined
                      }}
                    />
                  </Tooltip>
                </Space>
              </Space>
            </Col>
          </Row>
        </div>

        {/* Content Section */}
        <div style={{ padding: '24px' }}>
          <div style={{ marginBottom: 20 }}>
            <Paragraph style={{
              marginBottom: 16,
              color: "#666",
              fontSize: '14px',
              lineHeight: '1.6'
            }}>
              {builderConfig.description}
            </Paragraph>
          </div>

          {settings.enabled && (
            <>
              <Divider style={{ margin: "20px 0" }} />

              {(config.elements || config.coming_soon) && (
                <div style={{ marginTop: 16 }}>
                  <Collapse
                    ghost
                    size="small"
                    defaultActiveKey={["elements"]}
                    items={[
                      ...(config.elements ? [{
                        key: "elements",
                        label: (
                          <Space>
                            <StarOutlined style={{ color: builderConfig.color }} />
                            <Text strong>{builder === 'elementor' ? __("Available Widgets", "marqueex") : __("Available Blocks", "marqueex")}</Text>
                            <Badge count={Object.keys(config.elements).length}
                                   style={{ backgroundColor: builderConfig.color }} />
                          </Space>
                        ),
                        children: (
                          <div style={{
                            padding: '16px',
                            backgroundColor: '#fafafa',
                            borderRadius: 8,
                            border: `1px solid ${builderConfig.color}20`
                          }}>
                            <Row gutter={[12, 12]}>
                              {Object.keys(config.elements).map((widget) => (
                                <Col span={12} key={widget}>
                                  <div style={{
                                    padding: '8px 12px',
                                    backgroundColor: '#fff',
                                    borderRadius: 6,
                                    border: '1px solid #f0f0f0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                  }}>
                                    <Text style={{ fontSize: '13px' }}>
                                      {widget.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                                    </Text>
                                    <Switch
                                      checked={settings.elements?.[widget] ?? true}
                                      onChange={(checked) => onElementToggle(builder, widget, checked)}
                                      size="small"
                                    />
                                  </div>
                                </Col>
                              ))}
                            </Row>
                          </div>
                        ),
                      }] : []),
                      ...(config.coming_soon ? [{
                        key: "coming_soon",
                        label: (
                          <Space>
                            <FireOutlined style={{ color: '#faad14' }} />
                            <Text strong>{__("Coming Soon", "marqueex")}</Text>
                            <Badge count={Object.keys(config.coming_soon).length}
                                   style={{ backgroundColor: '#faad14' }} />
                          </Space>
                        ),
                        children: (
                          <div style={{
                            padding: '16px',
                            backgroundColor: '#fffbf0',
                            borderRadius: 8,
                            border: '1px solid #faad1420'
                          }}>
                            <Row gutter={[12, 12]}>
                              {Object.keys(config.coming_soon).map((item) => (
                                <Col span={12} key={item}>
                                  <div style={{
                                    padding: '8px 12px',
                                    backgroundColor: '#fff',
                                    borderRadius: 6,
                                    border: '1px solid #f0f0f0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    opacity: 0.7
                                  }}>
                                    <Text style={{ fontSize: '13px' }} type="secondary">
                                      {item.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                                    </Text>
                                    <Switch disabled size="small" />
                                  </div>
                                </Col>
                              ))}
                            </Row>
                          </div>
                        ),
                      }] : []),
                    ]}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </Card>
    );
};

export default BuilderCard;
